<template>
  <div class="analytics-tab">
    <recognition-filters
      :programs="programs"
      :program-id="programId"
      :selected-program="selectedProgram"
      :selected-date-range="selectedDateRange"
      :date-range-options="RECOGNITION_ANALYTICS_DATE_RANGES"
      :has-active-filters="hasActiveFilters"
      @update:selected-program="selectedProgram = $event"
      @update:selected-date-range="selectedDateRange = $event"
      @reset="resetFilters"
    >
      <template #prepend>
        <b-form-select
          v-if="hasDivisions"
          v-model="selectedDivision"
          :options="divisionOptions"
          class="recognition-filters__filter"
        />
      </template>
    </recognition-filters>

    <b-alert v-if="error && !loading" variant="danger" show>
      Unable to load analytics data. Please try adjusting your filters or try again later.
    </b-alert>
    <template v-else>
      <!-- Reward Spend -->
      <h5 class="analytics-tab__section-title">Reward Spend</h5>
      <b-row class="analytics-tab__spend-row">
        <b-col cols="12" lg="6">
          <div class="analytics-tab__stats-grid">
            <app-stat :stat="summary?.total_granted_minor" :formatter="toCurrency" description="Total granted" />
            <app-stat :stat="summary?.total_redeemed_minor" :formatter="toCurrency" description="Total redeemed" />
            <app-stat :stat="summary?.awards_granted" description="Awards granted" />
            <app-stat :stat="summary?.user_participation" suffix="%">
              <p class="analytics-tab__stat-description">
                User participation
                <span id="participation-info" class="analytics-tab__info-icon">
                  <font-awesome-icon :icon="['fal', 'info-circle']" />
                </span>
              </p>
              <b-tooltip target="participation-info" placement="top" variant="light">
                Share of eligible employees who've given or received recognition this period
              </b-tooltip>
            </app-stat>
          </div>
        </b-col>
        <b-col cols="12" lg="6">
          <div class="analytics-tab__chart-card">
            <div class="analytics-tab__chart-header">
              <h6 class="mb-0">Spend over time</h6>
              <b-form-select
                v-model="spendMode"
                :options="RECOGNITION_ANALYTICS_SPEND_MODES"
                class="analytics-tab__spend-toggle"
              />
            </div>
            <bar-chart
              v-if="chartSeriesX.values.length > 0"
              :series-x="chartSeriesX"
              :series-y="chartSeriesY"
              :chart-options="chartOptions"
              :chart-dataset-options="chartDatasetOptions"
            />
            <div v-else-if="!loading" class="analytics-tab__chart-empty">
              <p class="text-muted mb-0">No spend data available for the selected period.</p>
            </div>
          </div>
        </b-col>
      </b-row>

      <!-- Reward Redemption -->
      <h5 class="analytics-tab__section-title">Reward redemption</h5>
      <div class="analytics-tab__redemption-grid">
        <app-stat
          :stat="redemption?.cash?.amount_minor"
          :formatter="toCurrency"
          :subtitle="redemption?.cash ? `${redemption.cash.percentage}%` : undefined"
          description="Cash rewards"
        />
        <app-stat
          :stat="redemption?.savings?.amount_minor"
          :formatter="toCurrency"
          :subtitle="redemption?.savings ? `${redemption.savings.percentage}%` : undefined"
          description="Added to Savings"
        />
        <app-stat
          :stat="redemption?.gift_cards?.amount_minor"
          :formatter="toCurrency"
          :subtitle="redemption?.gift_cards ? `${redemption.gift_cards.percentage}%` : undefined"
          description="Gift cards"
        />
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import AppStat from '@/components/AppStat.vue';
import BarChart, { CHART_PRESET_OPTIONS } from '@/components/charts/BarChart.vue';
import RecognitionFilters from '@/pages/recognition/components/RecognitionFilters.vue';
import PaletteColors from '@/styles/palette-colors.module.scss';
import {
  RECOGNITION_ANALYTICS_DATE_RANGES,
  RECOGNITION_ANALYTICS_SPEND_MODES,
  getDateFromForRange,
} from '@/pages/recognition/constants';
import { getDatasetColor } from '@/utils/chart';
import { toCurrency } from '@/utils';
import useDivisionSelection from '@/state/composables/useDivisionSelection';
import ApiClient from '@/ApiClient';
import merge from 'lodash/merge';

const props = defineProps({
  programs: {
    type: Array,
    default: () => [],
  },
  programId: {
    type: String,
    default: null,
  },
});

const { divisions, hasDivisions } = useDivisionSelection();

const selectedDivision = ref(null);
const selectedProgram = ref(null);
const selectedDateRange = ref('last_12_months');
const spendMode = ref('granted');

const activeProgramId = computed(() => props.programId || selectedProgram.value);

const summary = ref(undefined);
const spendOverTime = ref(null);
const redemption = ref(undefined);
const loading = ref(false);
const error = ref(false);
const fetchRequestId = ref(0);

const divisionOptions = computed(() => {
  const options = [{ value: null, text: 'Entire company' }];
  for (const division of divisions.value) {
    options.push({
      value: division.division_id,
      text: division.name,
    });
  }
  return options;
});

const hasActiveFilters = computed(() => {
  const hasDivisionFilter = selectedDivision.value !== null;
  const hasProgramFilter = !props.programId && selectedProgram.value !== null;
  const hasDateFilter = selectedDateRange.value !== 'last_12_months';
  return hasDivisionFilter || hasProgramFilter || hasDateFilter;
});

const buildParams = () => {
  const params = {};
  if (selectedDivision.value) {
    params.division_ids = [selectedDivision.value];
  }
  const startDate = getDateFromForRange(selectedDateRange.value);
  if (startDate) {
    params.start_date = startDate;
  }
  const now = new Date();
  params.end_date = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(
    now.getDate(),
  ).padStart(2, '0')}`;
  return params;
};

const aggregateResults = results => {
  const summaryAgg = { total_granted_minor: 0, total_redeemed_minor: 0, awards_granted: 0, user_participation: 0 };
  const redemptionTotals = { cash: 0, savings: 0, gift_cards: 0 };
  const divisionMap = new Map();
  let labels = [];

  for (const [s, spend, r] of results) {
    summaryAgg.total_granted_minor += s?.total_granted_minor ?? 0;
    summaryAgg.total_redeemed_minor += s?.total_redeemed_minor ?? 0;
    summaryAgg.awards_granted += s?.awards_granted ?? 0;
    summaryAgg.user_participation += s?.user_participation ?? 0;

    if (spend?.labels?.length) {
      labels = spend.labels;
    }
    for (const ds of spend?.datasets || []) {
      const existing = divisionMap.get(ds.division_name);
      if (existing) {
        const gLen = Math.max(existing.granted_values_minor.length, ds.granted_values_minor?.length ?? 0);
        existing.granted_values_minor = Array.from(
          { length: gLen },
          (_, i) => (existing.granted_values_minor[i] ?? 0) + (ds.granted_values_minor?.[i] ?? 0),
        );
        const rLen = Math.max(existing.redeemed_values_minor.length, ds.redeemed_values_minor?.length ?? 0);
        existing.redeemed_values_minor = Array.from(
          { length: rLen },
          (_, i) => (existing.redeemed_values_minor[i] ?? 0) + (ds.redeemed_values_minor?.[i] ?? 0),
        );
      } else {
        divisionMap.set(ds.division_name, {
          division_name: ds.division_name,
          granted_values_minor: [...(ds.granted_values_minor || [])],
          redeemed_values_minor: [...(ds.redeemed_values_minor || [])],
        });
      }
    }

    redemptionTotals.cash += r?.cash?.amount_minor ?? 0;
    redemptionTotals.savings += r?.savings?.amount_minor ?? 0;
    redemptionTotals.gift_cards += r?.gift_cards?.amount_minor ?? 0;
  }

  const count = results.length || 1;
  summaryAgg.user_participation = Math.round(summaryAgg.user_participation / count);

  const redemptionTotal = redemptionTotals.cash + redemptionTotals.savings + redemptionTotals.gift_cards;
  const pct = v => (redemptionTotal > 0 ? Math.round((v / redemptionTotal) * 100) : 0);

  return {
    summary: summaryAgg,
    spend: { labels, datasets: [...divisionMap.values()] },
    redemption: {
      cash: { amount_minor: redemptionTotals.cash, percentage: pct(redemptionTotals.cash) },
      savings: { amount_minor: redemptionTotals.savings, percentage: pct(redemptionTotals.savings) },
      gift_cards: { amount_minor: redemptionTotals.gift_cards, percentage: pct(redemptionTotals.gift_cards) },
    },
  };
};

const fetchAnalytics = async () => {
  const programIds = activeProgramId.value
    ? [activeProgramId.value]
    : props.programs.map(p => p.recognition_award_program_id);

  if (programIds.length === 0) {
    return;
  }

  fetchRequestId.value += 1;
  const currentRequestId = fetchRequestId.value;

  loading.value = true;
  error.value = false;
  summary.value = undefined;
  spendOverTime.value = null;
  redemption.value = undefined;

  try {
    const params = buildParams();
    const results = await Promise.all(
      programIds.map(id =>
        Promise.all([
          ApiClient.getRecognitionAnalyticsSummary(id, params),
          ApiClient.getRecognitionAnalyticsSpendOverTime(id, params),
          ApiClient.getRecognitionAnalyticsRedemptionBreakdown(id, params),
        ]),
      ),
    );

    if (currentRequestId !== fetchRequestId.value) {
      return;
    }

    const aggregated = aggregateResults(results);
    summary.value = aggregated.summary;
    spendOverTime.value = aggregated.spend;
    redemption.value = aggregated.redemption;
  } catch (e) {
    if (currentRequestId !== fetchRequestId.value) {
      return;
    }
    summary.value = null;
    spendOverTime.value = null;
    redemption.value = null;
    error.value = true;
  } finally {
    if (currentRequestId === fetchRequestId.value) {
      loading.value = false;
    }
  }
};

const resetFilters = () => {
  selectedDivision.value = null;
  selectedProgram.value = null;
  selectedDateRange.value = 'last_12_months';
};

// Chart data

const chartSeriesX = computed(() => ({
  values: (spendOverTime.value?.labels || []).map(label => ({ name: label })),
}));

const chartSeriesY = computed(() => {
  if (!spendOverTime.value?.datasets) {
    return [];
  }
  const key = spendMode.value === 'granted' ? 'granted_values_minor' : 'redeemed_values_minor';
  return spendOverTime.value.datasets.map((dataset, index) => ({
    name: dataset.division_name,
    values: (dataset[key] || []).map(v => v / 100),
    color: PaletteColors[getDatasetColor(index)],
  }));
});

const chartOptions = computed(() =>
  merge({}, CHART_PRESET_OPTIONS.CURRENCY, {
    scales: {
      x: { stacked: true },
      y: { stacked: true },
    },
    plugins: {
      legend: {
        display: true,
        position: 'bottom',
        labels: {
          usePointStyle: true,
          pointStyle: 'circle',
          padding: 20,
        },
      },
    },
  }),
);

const chartDatasetOptions = { borderRadius: 4, maxBarThickness: 40 };

// Watch filters and refetch
watch([selectedDivision, activeProgramId, selectedDateRange], () => {
  fetchAnalytics();
});

onMounted(() => {
  fetchAnalytics();
});
</script>

<style scoped lang="scss">
.analytics-tab {
  &__section-title {
    font-weight: 600;
    margin-bottom: 1rem;
    color: var(--palette-color-default);
  }

  &__spend-row {
    margin-bottom: 1.5rem;

    --bs-gutter-x: 1rem;
  }

  &__stats-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
    margin-bottom: 1rem;

    @media (min-width: 992px) {
      height: 100%;
      margin-bottom: 0;
    }
  }

  &__redemption-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 1rem;

    @media (min-width: 768px) {
      grid-template-columns: repeat(3, 1fr);
    }
  }

  &__chart-card {
    background-color: var(--palette-color-base-white);
    border-radius: 4px;
    box-shadow: var(--depth-2);
    padding: 1.25rem 1.5rem;
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  &__chart-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
  }

  &__spend-toggle {
    width: auto;
    min-width: 120px;
    border-color: var(--palette-color-default-lighten-80);
    padding: 0.375rem 0.75rem;
    font-size: 0.875rem;
  }

  &__chart-empty {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1;
    min-height: 200px;
  }

  :deep(.app-stat__value) {
    color: var(--palette-color-default);
  }

  :deep(.app-stat__description) {
    color: var(--palette-color-default-lighten-30);
  }

  :deep(.app-stat__subtitle) {
    font-size: 20px;
    font-weight: 600;
    color: var(--palette-color-default-lighten-30);
  }

  &__stat-description {
    font-size: 14px;
    font-weight: 400;
    line-height: 1.3;
    margin: 0;
    color: var(--palette-color-default-lighten-30);
  }

  &__info-icon {
    cursor: pointer;
    color: var(--palette-color-default-lighten-40);
    font-size: 0.875rem;
    margin-left: 0.25rem;
  }
}
</style>
